// src/pages/Analyzer.tsx
import React, { useEffect, useRef, useState } from "react";
import { getUsage, canUse, track, Limits } from "../lib/plan";
// import { getSession, saveJob as saveJobDb, listSavedJobs, type SavedJob } from "../lib/db";
import { supabase } from "../lib/supabaseClient";
import { supaRest } from "../lib/supaRest";
import { ensureUser } from "../lib/db";  // ← move here with the other imports
import { useSpeechToText } from "../hooks/useSpeechToText";
import { apiFetch } from "../lib/api";
import { useAuth } from "../auth/AuthProvider"; // if you want to gate by login

const PAGE_SIZE = 10; // how many jobs to show at once


// --- Local saves (single source of truth) ---
const LS_KEY = "savedJobs_v1";

type SavedStore = Record<string, any>;      // url -> full job object
type SavedMap   = Record<string, boolean>;  // url -> true

function readSavedStore(): SavedStore {
  try { return JSON.parse(localStorage.getItem(LS_KEY) ?? "{}"); }
  catch { return {}; }
}

function writeSavedStore(store: SavedStore) {
  localStorage.setItem(LS_KEY, JSON.stringify(store));
  // let other components know immediately
  window.dispatchEvent(new Event("savedjobs:changed"));
}

function storeToMap(store: SavedStore): SavedMap {
  const m: SavedMap = {};
  Object.keys(store).forEach((url) => (m[url] = true));
  return m;
}

// Optional: clean out bad entries created earlier
function repairStore(): SavedStore {
  const s = readSavedStore();
  const cleaned: SavedStore = {};
  Object.values(s).forEach((j: any) => {
    if (j && typeof j.url === "string" && j.url.trim()) cleaned[j.url] = j;
  });
  writeSavedStore(cleaned);
  return cleaned;
}


// Adzuna-supported countries (codes must match Adzuna API)
const ADZUNA_COUNTRIES = [
  { code: "us", name: "United States" },
  { code: "gb", name: "United Kingdom" },
  { code: "se", name: "Sweden" },
  { code: "de", name: "Germany" },
  { code: "fr", name: "France" },
  { code: "ca", name: "Canada" },
  { code: "au", name: "Australia" },
  { code: "nz", name: "New Zealand" },
  { code: "in", name: "India" },
  { code: "sg", name: "Singapore" },
  { code: "br", name: "Brazil" },
  { code: "za", name: "South Africa" },
  { code: "es", name: "Spain" },
  { code: "it", name: "Italy" },
  { code: "nl", name: "Netherlands" },
  { code: "pl", name: "Poland" },
  { code: "mx", name: "Mexico" },
  { code: "ch", name: "Switzerland" },
  { code: "at", name: "Austria" },
  { code: "ie", name: "Ireland" },
  { code: "dk", name: "Denmark" },
  { code: "no", name: "Norway" },
  { code: "fi", name: "Finland" },
  { code: "pt", name: "Portugal" },
  { code: "cz", name: "Czech Republic" },
] as const;


type AdzunaCountryCode = (typeof ADZUNA_COUNTRIES)[number]["code"];

// --- normalize country name -> 2-letter code (e.g., "Sweden" -> "se")
const nameToCode = new Map(ADZUNA_COUNTRIES.map(c => [c.name.toLowerCase(), c.code]));
function normalizeCountryCode(input?: string | null): string {
  if (!input) return "";
  const s = input.trim().toLowerCase();
  if (ADZUNA_COUNTRIES.some(c => c.code === s)) return s; // already "se","de",...
  return nameToCode.get(s) ?? "";
}

// HERE ► tiny helper to show "France" instead of "fr"
const CODE_TO_NAME = new Map<string, string>(
  ADZUNA_COUNTRIES.map(c => [c.code, c.name])
);

// Special aliases that cover common user phrasing, incl. short forms US/UK
const COUNTRY_FREEFORM_ALIASES: Array<[RegExp, string]> = [
  // United States (covers “United States”, “U.S.”, “USA”, and bare “US”)
  [/\b(?:united\s+states(?:\s+of\s+america)?|u\.?s\.?a?\.?|us)\b/i, "us"],

  // United Kingdom (covers “United Kingdom”, “U.K.”, “UK”, GB & nations)
  [/\b(?:united\s+kingdom|u\.?k\.?|uk|great\s+britain|england|scotland|wales|northern\s+ireland)\b/i, "gb"],
];


function detectCountryFromText(text: string): string {
  const t = text.toLowerCase().trim();

  for (const [re, code] of COUNTRY_FREEFORM_ALIASES) {
    if (re.test(t)) return code;
  }
  for (const c of ADZUNA_COUNTRIES) {
    if (t.includes(c.name.toLowerCase())) return c.code;
  }
  // safest: don't try to read 2-letter codes at all
  return "";
}

// --- Floating mic button (uses useSpeechToText) ---
function MicButton({
  listening,
  supported,
  onToggle,
  className = "",
  title,
}: {
  listening: boolean;
  supported: boolean;
  onToggle: () => void;
  className?: string;
  title?: string;
}) {
  return (
    <button
      type="button"
      onClick={onToggle}
      title={supported ? (title || (listening ? "Stop" : "Speak")) : "Voice not supported"}
      className={
        `relative h-10 w-10 rounded-full
         ${supported ? "bg-zinc-800 hover:bg-zinc-700" : "bg-zinc-700/50 cursor-not-allowed"}
         text-white shadow-lg flex items-center justify-center
         transition-colors ${className}`
      }
      aria-pressed={listening}
      disabled={!supported}
    >
      {/* icon */}
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true">
        <path fill="currentColor"
          d="M12 14a3 3 0 0 0 3-3V6a3 3 0 0 0-6 0v5a3 3 0 0 0 3 3Zm5-3a5 5 0 0 1-10 0H5a7 7 0 0 0 6 6.92V21h2v-3.08A7 7 0 0 0 19 11h-2Z"/>
      </svg>

      {/* pulsing ring when listening */}
      {listening && (
        <span className="absolute inline-flex h-full w-full rounded-full bg-primary/30 animate-ping"></span>
      )}
    </button>
  );
}

// Add this helper here
type JobType =
  | "Any"
  | "Full-time"
  | "Part-time"
  | "Contract"
  | "Internship"
  | "Temporary"
  | "Freelance";

function detectEmploymentTypeFromText(t: string): JobType {
  const s = t.toLowerCase();

  // Internship first (very specific)
  if (/\b(internship|intern|trainee|student position)\b/.test(s)) {
    return "Internship";
  }

  // Contract
  if (/\b(contract|contractor|fixed[-\s]?term|consultant|consultancy)\b/.test(s)) {
    return "Contract";
  }

  // Temporary / seasonal
  if (/\b(temporary|temp|seasonal)\b/.test(s)) {
    return "Temporary";
  }

  // Freelance / gig
  if (/\b(freelance|freelancer|self[-\s]?employed|gig)\b/.test(s)) {
    return "Freelance";
  }

  // Part-time
  if (/\bpart[-\s]?time\b/.test(s)) {
    return "Part-time";
  }

  // Full-time / permanent
  if (/\bfull[-\s]?time\b/.test(s) || /\bpermanent\b/.test(s)) {
    return "Full-time";
  }

  return "Any";
}


// HERE ► localized country-name aliases used for filtering by country
const COUNTRY_NAME_ALIASES: Record<string, string[]> = {
  se: ["sweden", "sverige"],
  de: ["germany", "deutschland"],
  fr: ["france", "français", "francaise", "française"],
  nl: ["netherlands", "holland", "nederland"],
  es: ["spain", "españa"],
  it: ["italy", "italia"],
  no: ["norway", "norge"],
  dk: ["denmark", "danmark"],
  fi: ["finland", "suomi"],
  pt: ["portugal"],
  pl: ["poland", "polska"],
  cz: ["czech republic", "czechia", "česko"],
  ie: ["ireland", "éire"],
  gb: ["united kingdom", "great britain", "england", "uk", "gb"],
  us: ["united states", "usa", "us", "america"],
  ca: ["canada"],
  au: ["australia"],
  nz: ["new zealand", "nz"],
  sg: ["singapore"],
  br: ["brazil", "brasil"],
  za: ["south africa"],
  mx: ["mexico", "méxico"],
  ch: ["switzerland", "schweiz", "suisse", "svizzera"],
  at: ["austria", "österreich"],
  // HERE ► add missing aliases so we can exclude those countries properly
  gr: ["greece", "ελλάδα", "ellada", "ellás"],
  mt: ["malta"],
  cy: ["cyprus", "kypros", "κύπρος"],

};


type AnalyzerProps = {
  userPlan?: "free" | "premium";
  onHitPaywall?: () => void;
};


type Bullet = { original: string; rewrite: string; reason: string };

type AnalyzeResponse = {
  match_score: number;
  top_common_keywords: string[];
  missing_keywords: string[];
  suggestions: Bullet[];
};

const API = process.env.REACT_APP_API ?? "http://localhost:8000";

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

export type Usage = {
  search: number;
  analyze: number;
  export: number;
};

export default function Analyzer({
  userPlan = "free",
  onHitPaywall,
}: AnalyzerProps) {
  // start with zeros, then update via setUsage(await getUsage())
  const [usage, setUsage] = React.useState<Usage>({
    search: 0,
    analyze: 0,
    export: 0,
  });

  const [savingId, setSavingId] = useState<string | null>(null);
  const [isAuthed, setIsAuthed] = useState(false);
  
  useEffect(() => {
    (async () => {
      try {
        setUsage(await getUsage());
      } catch (e) {
        console.error("Failed to load usage:", e);
      }
    })();
  }, []);

  // On mount: hydrate "saved" pill map from localStorage
  useEffect(() => {
    setSaved(storeToMap(readSavedStore()));
  }, []);

  useEffect(() => {
  // initial read
  supabase.auth.getSession().then(({ data }) => {
    setIsAuthed(!!data?.session);
  });

  // keep it updated
  const { data: sub } = supabase.auth.onAuthStateChange((_evt, session) => {
    setIsAuthed(!!session);
  });

  return () => sub.subscription.unsubscribe();
}, []);

function requireSignIn(): boolean {
  if (!isAuthed) {
    // optional toast/alert if you want feedback
    // alert("Please sign in to save jobs.");
    window.location.href = "/auth";   // <-- your sign-in route
    return false;
  }
  return true;
}


  // Analyzer state
  const [jd, setJd] = useState("");
  const [resume, setResume] = useState("");
  const [resumeFile, setResumeFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<AnalyzeResponse | null>(null);
  // export usage gate
  const [exportOk, setExportOk] = useState(true);

  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const ok = await canUse("export", userPlan);
        if (alive) setExportOk(ok);
      } catch {}
    })();
    return () => { alive = false; };
  }, [userPlan]);

  const [error, setError] = useState<string | null>(null);

  // Cover letter state
  const [coverLetter, setCoverLetter] = useState<string>("");
  const [genLoading, setGenLoading] = useState(false);

  const [coverTone, setCoverTone] = useState<"neutral" | "formal" | "friendly" | "concise">("neutral");
  const [coverLang, setCoverLang] = useState<"en" | "sv" | "de" | "fr" | "es">("en");


  // 🔹 NEW – “what are you looking for?” text
  const [intent, setIntent] = useState("");


  // put near other useState
  const jdRef = React.useRef<HTMLTextAreaElement>(null);
  const [flashJD, setFlashJD] = React.useState(false);

  // right next to jdRef / flashJD
  const resultRef = React.useRef<HTMLDivElement>(null);
  const coverRef  = React.useRef<HTMLDivElement>(null);

  const [flashResult, setFlashResult] = React.useState(false);


  // near your other refs/state
  const resumeInputRef = React.useRef<HTMLInputElement>(null);
  const [fileInputKey, setFileInputKey] = React.useState(0); // fallback for stubborn browsers

  // summary modal state
  const [summaryFor, setSummaryFor] = useState<any | null>(null);
  const [summaryData, setSummaryData] = useState<{tldr:string[], must_haves:string[], concerns:string[]} | null>(null);
  const [summLoading, setSummLoading] = useState<Record<string, boolean>>({});
  const [summError, setSummError] = useState<string | null>(null);


  // Job search state
  const [jobKeyword, setJobKeyword] = useState("");
  const [jobUrl, setJobUrl] = useState("");
  const [rssUrl, setRssUrl] = useState("");
  // Exclude terms: words/phrases to hide from results
  const [excludeText, setExcludeText] = useState("");

  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [jobSearchLoading, setJobSearchLoading] = useState(false);

  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [page, setPage] = useState(1);

  //const [jobType, setJobType] = useState<"any" | "full" | "part">("any");
  const [jobType, setJobType] = useState<JobType>("Any");


  // Message to show when no results / fallback, etc.
  const [searchMessage, setSearchMessage] = useState<string>("");
  // URL -> saved?
  // URL -> saved? (hydrated from DB so the “Saved” button is green after refresh)
  const [saved, setSaved] = React.useState<Record<string, boolean>>({});
  const [savedList, setSavedList] = useState<any[]>([]);

  const ENABLE_QUICK_SUMMARY = true;

  // New For The CV
  const [cvInput, setCvInput] = useState("");
  const [generatedCv, setGeneratedCv] = useState("");
  const [cvLoading, setCvLoading] = useState(false);

  // AI CV Builder
  const [coachInput, setCoachInput] = useState("");
  const [showCvCoach, setShowCvCoach] = useState(false);

  // Short UI language code the backend understands
  const [uiLang, setUiLang] = useState<"en"|"sv"|"de"|"fr"|"es">("en");

  // helper: map browser speech tag -> short code the API wants
  const mapSpeechToShort = (tag: string): "en"|"sv"|"de"|"fr"|"es" => {
    if (tag.startsWith("sv")) return "sv";
    if (tag.startsWith("de")) return "de";
    if (tag.startsWith("fr")) return "fr";
    if (tag.startsWith("es")) return "es";
    return "en";
  };

  // INSERT THIS EFFECT RIGHT HERE
  useEffect(() => {
    (async () => {
      try {
        const {
          data: { session },
          error: sessionError,
        } = await supabase.auth.getSession();

        if (sessionError) {
          console.error("Error getting session in Analyzer hydrate:", sessionError);
          return;
        }
        if (!session?.user) {
          console.log("Analyzer hydrate: no logged-in user, skipping saved jobs load.");
          return;
        }

        const { data, error } = await supabase
          .from("saved_jobs")
          .select("url")
          .eq("user_id", session.user.id);

        if (error) {
          console.error("Failed to load saved jobs in Analyzer:", error);
          return;
        }

        const m: Record<string, boolean> = {};
        (data || []).forEach((row: { url: string | null }) => {
          if (row.url) m[row.url] = true;
        });

        console.log("Analyzer hydrate: saved jobs map =", m);
        setSaved(m);
      } catch (e) {
        console.error("Unexpected error hydrating saved jobs:", e);
      }
    })();
  }, []);

  // Save a job via backend (Authorization handled by apiFetch)
  // put near top of the file if you don't already have it:
  const API_BASE = (process.env.REACT_APP_API ?? "http://localhost:8000").replace(/\/+$/,"");

  // Button handlers used by the result cards
  function notifySavedJobsChange() {
    // same-tab listeners
    window.dispatchEvent(new Event("savedjobs:changed"));
    // cross-tab listeners
    localStorage.setItem("__savedjobs_ping__", String(Date.now()));
  }

  function saveJobLocal(j: any) {
    if (!isAuthed) {
      alert("Please sign in to save jobs.");
      return;
    }
    if (!j?.url) return;
    setSavingId(j.url);
    try {
      const store = readSavedStore();
      store[j.url] = { ...j, saved_at: Date.now() };
      writeSavedStore(store);
      setSaved(prev => ({ ...prev, [j.url]: true }));
      notifySavedJobsChange();
    } finally {
      setSavingId(null);
    }
  }

  function unsaveJobLocal(url?: string | null) {
    if (!isAuthed) {
      alert("Please sign in first.");
      return;
    }
    if (!url) return;
    setSavingId(url);
    try {
      const store = readSavedStore();
      delete store[url];
      writeSavedStore(store);
      setSaved(prev => {
        const next = { ...prev };
        delete next[url];
        return next;
      });
      notifySavedJobsChange();
    } finally {
      setSavingId(null);
    }
  }


  // NEW: natural language query
  const [naturalQuery, setNaturalQuery] = useState("");

  const mic = useSpeechToText(); // defaults to browser language


  // store ONE country code at a time in the UI (default: Sweden, pick any you like)
  const [jobCountry, setJobCountry] = useState<string>("");
  const [jobLocation, setJobLocation] = useState("");

  
  const [parsedIntent, setParsedIntent] = useState<any | null>(null);

  // Resume upload handl

  const handleResumeUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setResumeFile(file);

    const form = new FormData();
    form.append("file", file);

    console.log("POST ->", `${API}/parse-resume`); // should be :8000/parse-resume

    const res = await fetch(`${API}/parse-resume`, {
      method: "POST",
      body: form,
    });

    if (!res.ok) {
      const text = await res.text();
      throw new Error(`Upload failed (${res.status}): ${text}`);
    }
    const data = await res.json();        // server returns { text: "..." }
    setResume(data.text || "");
  };

  // HERE ► final country-aware filter (checks title/company too)
  // STRICT country filter — trust the feed, not the text
  const filterByLocation = (
    jobs: any[],
    location?: string,
    countryCode?: string
  ) => {
    const locLower = (location || "").trim().toLowerCase();
    const codeLower = (countryCode || "").trim().toLowerCase();

    // If user didn’t pick a country, keep everything (and only apply city filter)
    if (!codeLower) {
      return jobs.filter(j => (locLower ? ((j.location || "").toLowerCase().includes(locLower)) : true));
    }

    // Build the allowlist of sources that are unquestionably for this country
    const allowedSources = new Set<string>([`adzuna-${codeLower}`]);
    if (codeLower === "se") allowedSources.add("platsbanken");
    if (codeLower === "us") allowedSources.add("usajobs");

    return jobs.filter((j) => {
      const src = (j.source_code || j.source || "").toLowerCase();
      const jobLoc = (j.location || "").toLowerCase();

      // Must come from the allowed source(s)
      if (!allowedSources.has(src)) return false;

      // If user typed a city, require it in the location text
      if (locLower && !jobLoc.includes(locLower)) return false;

      return true;
    });
  };

  // Filter helpers for employment type
  type JobType =
  | "Any"
  | "Full-time"
  | "Part-time"
  | "Contract"
  | "Internship"
  | "Temporary"
  | "Freelance";

function filterByJobType(jobs: any[], jobType: JobType) {
  if (!jobType || jobType === "Any") return jobs;

  const has = (j: any, ...phrases: string[]) => {
    const t = `${j.title || ""} ${j.jd_text || ""}`.toLowerCase();
    return phrases.some((p) => t.includes(p));
  };

  return jobs.filter((j) => {
    switch (jobType) {
      case "Full-time":
        return has(j, "full-time", "full time", "permanent");
      case "Part-time":
        return has(j, "part-time", "part time");
      case "Contract":
        return has(j, "contract", "contractor", "fixed-term", "fixed term", "consultancy", "consultant");
      case "Internship":
        return has(j, "internship", "intern");
      case "Temporary":
        return has(j, "temporary", "temp");
      case "Freelance":
        return has(j, "freelance", "freelancer", "gig");
      default:
        return true;
    }
  });
}

// --- Exclude helpers -------------------------------------------------
function parseExcludeTerms(s: string): string[] {
  // split by commas or spaces, but keep quoted phrases (e.g. "customer success")
  const terms: string[] = [];
  const rx = /"([^"]+)"|(\S+)/g;
  let m: RegExpExecArray | null;
  while ((m = rx.exec(s)) !== null) {
    const raw = (m[1] || m[2]).toLowerCase().trim();
    if (!raw) continue;
    // tiny normalization: treat sr / sr. as "senior"
    if (raw === "sr" || raw === "sr.") terms.push("senior");
    terms.push(raw);
  }
  return Array.from(new Set(terms));
}

function filterByExcludes(jobs: any[], excludeText: string) {
  const terms = parseExcludeTerms(excludeText);
  if (terms.length === 0) return jobs;

  return jobs.filter((j) => {
    const hay = `${j.title || ""} ${j.company || ""} ${j.jd_text || ""}`.toLowerCase();
    // hide job if ANY excluded term appears
    return !terms.some((t) => hay.includes(t));
  });
}

// --- Employment type helper (top level, once) ---
function detectEmploymentTypeFromText(t: string): JobType {
  const s = t.toLowerCase();

  // English
  if (/\bintern(ship)?\b/.test(s)) return "Internship";
  if (/\bcontract(or)?\b|fixed[-\s]?term|consult(ant|ancy)\b/.test(s)) return "Contract";
  if (/\btemporary|temp|seasonal\b/.test(s)) return "Temporary";
  if (/\bfreelance|freelancer|gig|self[-\s]?employed\b/.test(s)) return "Freelance";
  if (/\bpart[-\s]?time\b/.test(s)) return "Part-time";
  if (/\bfull[-\s]?time|permanent\b/.test(s)) return "Full-time";

  // Svenska
  if (/\bpraktik|trainee\b/.test(s)) return "Internship";
  if (/\bvisstid|konsult|uppdrag|tidsbegr[aä]nsad\b/.test(s)) return "Contract";
  if (/\btillf[aä]llig|s[aä]song|vikariat\b/.test(s)) return "Temporary";
  if (/\bfrilans\b/.test(s)) return "Freelance";
  if (/\bdeltid\b/.test(s)) return "Part-time";
  if (/\bheltid|tillsvidare\b/.test(s)) return "Full-time";

  // Deutsch
  if (/\bpraktikum\b/.test(s)) return "Internship";
  if (/\bbefristet|zeitvertrag|vertrag\b/.test(s)) return "Contract";
  if (/\btempor[aä]r|saisonal|aushilfe\b/.test(s)) return "Temporary";
  if (/\bfreiberuflich\b/.test(s)) return "Freelance";
  if (/\bteilzeit\b/.test(s)) return "Part-time";
  if (/\bvollzeit\b/.test(s)) return "Full-time";

  // Français
  if (/\bstage\b/.test(s)) return "Internship";
  if (/\bcdd|int[eé]rim|contrat\b/.test(s)) return "Contract";
  if (/\btemporaire|saisonnier\b/.test(s)) return "Temporary";
  if (/\bfreelance|ind[eé]pendant\b/.test(s)) return "Freelance";
  if (/\btemps\s*partiel\b/.test(s)) return "Part-time";
  if (/\btemps\s*plein\b/.test(s)) return "Full-time";

  // Español
  if (/\bpr[aá]cticas|pasant[ií]a\b/.test(s)) return "Internship";
  if (/\bcontrato\b/.test(s)) return "Contract";
  if (/\btemporal|estacional\b/.test(s)) return "Temporary";
  if (/\bfreelance|aut[oó]nomo\b/.test(s)) return "Freelance";
  if (/\bmedio\s*tiempo|tiempo\s*parcial\b/.test(s)) return "Part-time";
  if (/\btiempo\s*completo\b/.test(s)) return "Full-time";

  return "Any";
}

  // --- Job card helpers -----------------------------------------------
  function getFlagFromSource(sourceCode?: string): string {
    // expects things like "adzuna-de", "usajobs", "platsbanken"
    if (!sourceCode) return "";
    const m = /adzuna-([a-z]{2})/i.exec(sourceCode);
    const code = (m?.[1] || "").toLowerCase();
    const flags: Record<string, string> = {
      us: "🇺🇸", gb: "🇬🇧", se: "🇸🇪", de: "🇩🇪", fr: "🇫🇷",
      ca: "🇨🇦", au: "🇦🇺", nz: "🇳🇿", in: "🇮🇳", sg: "🇸🇬",
      br: "🇧🇷", za: "🇿🇦", es: "🇪🇸", it: "🇮🇹", nl: "🇳🇱",
      pl: "🇵🇱", mx: "🇲🇽", ch: "🇨🇭", at: "🇦🇹", ie: "🇮🇪",
      dk: "🇩🇰", no: "🇳🇴", fi: "🇫🇮", pt: "🇵🇹", cz: "🇨🇿",
    };
    return flags[code] || "";
  }

  function extractSalary(text?: string): string {
    if (!text) return "";
    const m = text.match(
      /(?:\$|€|£|sek|dkk|nok|inr|usd|eur)\s?[\d,.]+(?:\s*[-–]\s*[\d,.]+)?/i
    );
    return m ? m[0] : "";
  }
  function detectWorkMode(j: any): "Remote" | "Hybrid" | "On-site" | "" {
    const t = `${j.title || ""} ${j.jd_text || ""} ${j.location || ""}`.toLowerCase();
    if (/\b(remote|work\s*from\s*home|wfh)\b/.test(t)) return "Remote";
    if (/\bhybrid\b/.test(t)) return "Hybrid";
    if (/\bon[\-\s]?site\b/.test(t)) return "On-site";
    return "";
  }


  function fetchWithTimeout(input: RequestInfo, init?: RequestInit, ms = 20000) {
  return new Promise<Response>((resolve, reject) => {
    const timer = setTimeout(() => reject(new Error("Request timed out")), ms);
    fetch(input, init)
      .then((res) => { clearTimeout(timer); resolve(res); })
      .catch((err) => { clearTimeout(timer); reject(err); });
  });
}


  /**
   * Core search runner – both AI and manual search go through this.
   * It:
   * - builds the query to /api/jobs/search
   * - calls the backend
   * - filters results by location/country
   */
  const runSearch = async (opts: {
  keyword: string;
  location?: string;
  countryCode?: string;
  url?: string;
  rssUrl?: string;
  useRss: boolean;  // whether to include global RSS feeds
}) => {
  const allSupported = ADZUNA_COUNTRIES.map((c) => c.code).join(",");

  //const cn = normalizeCountryCode(opts.countryCode || "");
  //const countries = cn ? cn
    //: (userPlan === "premium" ? allSupported : Limits.free.adzunaCountries.join(","));
  const cn = normalizeCountryCode(opts.countryCode || "");
  const countries = cn ? cn : allSupported;
  // HERE ► include country-specific sources only when relevant
  // HERE ► country-safe source selection
  const code = normalizeCountryCode(opts.countryCode || "");
  const isCountrySearch = !!code;

  const sourcesList = [
    // Global sources disabled when a country is selected
    ...(isCountrySearch ? [] : ["schema", ...(opts.useRss ? ["rss"] : [])]),
    // Country-specific feeds
    ...(code === "se" ? ["platsbanken"] : []),
    ...(code === "us" ? ["usajobs"] : []),
    // Always include Adzuna (it obeys the countries param)
    "adzuna",
  ];

  const sources = sourcesList.join(",");


  const params = new URLSearchParams({
    q: opts.keyword || "software engineer",
    sources,
    countries,
  });

  if (opts.location) params.append("location", opts.location);
  if (opts.url) params.append("url", opts.url);
  if (opts.rssUrl) params.append("rss_url", opts.rssUrl);

  const res = await fetch(`${API}/api/jobs/search?` + params.toString());
  const data: any[] = await res.json();

// DEBUG – what we asked for & first few
console.log("[runSearch] params", Object.fromEntries(params));
console.log("[type] jobType state =", jobType, "total fetched =", (data || []).length);
console.log("[runSearch] raw jobs", (data || []).slice(0, 5));

// 1) by employment type
const typeFiltered = filterByJobType(data || [], jobType);
console.log("[type] after type filter =", typeFiltered.length);

// 2) exclude keywords/companies
const excludedFiltered = filterByExcludes(typeFiltered, excludeText);
console.log("[exclude] after exclude filter =", excludedFiltered.length);

// 3) then country/city
const filtered = filterByLocation(excludedFiltered, opts.location, opts.countryCode);
console.log("[location] after location filter =", filtered.length);
console.log("[runSearch] filtered jobs", filtered.slice(0, 5));

setSearchResults(filtered);
return filtered;


};
  // --- AI SMART SEARCH (uses /ai/intent) ---
  const smartSearch = async () => {
    if (!naturalQuery.trim()) {
      alert("Describe what you want first.");
      return;
    }

    if (!canUse("search", userPlan)) {
      onHitPaywall?.();
      alert("Free plan limit reached today. Upgrade for unlimited searches.");
      return;
    }

    try {
      setJobCountry("");
      setJobLocation("");
      
      //setJobSearchLoading(true);
      setSearchResults([]);
      setSearchMessage("");  
      setParsedIntent(null); 
      setVisibleCount(PAGE_SIZE);
      setPage(1);

      // 1) Ask backend to parse the intent
      const intentRes = await fetch(`${API}/api/ai/intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text: naturalQuery, lang: uiLang }),
      });

      const intent = await intentRes.json();
      console.log("AI intent:", intent);

      // 2) Build a keyword string from role_type + keywords (if AI gave us any)
      const combinedKeywords = [
        intent.role_type || "",
        ...(Array.isArray(intent.keywords) ? intent.keywords : []),
      ]
        .join(" ")
        .trim();

      // 2b) Fallback: derive simple keywords from the natural query itself
      const cleanedNatural = naturalQuery
        .replace(/[^a-zA-Z0-9\s]/g, " ")
        .toLowerCase()
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 10)
        .join(" ");

      const keyword =
        combinedKeywords ||
        jobKeyword ||
        cleanedNatural ||
        "software engineer";

      // 👇 3b) Employment type from AI or text → full JobType
      let detectedType: JobType = "Any";
      const rawType = (intent.employment_type || intent.job_type || "")
        .toString()
        .toLowerCase();

      // Prefer AI's explicit type if present
      if (rawType.includes("intern")) {
        detectedType = "Internship";
      } else if (rawType.includes("contract")) {
        detectedType = "Contract";
      } else if (rawType.includes("freelanc")) {
        detectedType = "Freelance";
      } else if (rawType.includes("temp")) {
        detectedType = "Temporary";
      } else if (rawType.includes("part")) {
        detectedType = "Part-time";
      } else if (rawType.includes("full")) {
        detectedType = "Full-time";
      } else {
        // Fallback: infer from the user's sentence
        detectedType = detectEmploymentTypeFromText(naturalQuery);
      }

      // reflect in UI (dropdown)
      setJobType(detectedType);

      // 3) Country + location from AI, with safe fallbacks
    const aiCountryRaw = (intent.country ?? "").toString();
    let countryCode = normalizeCountryCode(aiCountryRaw);
    
    // Step 1: Detect from user text (regex-based fallback)
    const detectedFromText = detectCountryFromText(naturalQuery);

    // Step 2: If AI guessed a country but it’s not in the user text, and our regex detects something else, prefer the regex
    if (countryCode && detectedFromText && countryCode !== detectedFromText) {
      countryCode = detectedFromText;
    }

    // ➤ Add this check: If the intent.country is "India" but not clearly in the prompt, ignore it
    if (countryCode === "in" && !/\bindia\b/i.test(naturalQuery)) {
      countryCode = "";
    }

    // Fallback: detect from the sentence (handles UK/USA)
    if (!countryCode) {
      const detected = detectCountryFromText(naturalQuery);
      if (detected) countryCode = detected;
    }

    // Final fallback: do NOT reuse previous dropdown value (prevents “India” sticking)
    if (countryCode) setJobCountry(countryCode);

    const location: string =
      intent.location ||
      jobLocation || "";


      // Reflect what AI understood back into the visible inputs
      if (countryCode) setJobCountry(countryCode);   // must be "se","de","fr",...
      if (intent.location) setJobLocation(intent.location);
      if (combinedKeywords) setJobKeyword(combinedKeywords);

      // 4) If the user clearly wants remote → allow RSS even with a location.
//          // Clear any old message
      setSearchMessage("");

      // 4) If we have a location or explicit country → no RSS
      //    (we treat "remote jobs in Sundsvall" as *Sundsvall-focused* first)
      const useRss = !(location || countryCode);

      // 5) Run the actual search (Sundsvall-focused)
      const primaryJobs = await runSearch({
        keyword,
        location,
        countryCode,
        useRss,
      });

      // HERE ► universal fallback/message for all countries
      if (primaryJobs.length === 0) {
        const countryName = CODE_TO_NAME.get(countryCode) || (countryCode ? countryCode.toUpperCase() : "");

        // If user gave a city + country → widen to the whole country
        if (location && countryCode) {
          const fallbackJobs = await runSearch({
            keyword,
            location: "",          // widen
            countryCode,
            useRss: false,         // still avoid global feeds
          });

          if (fallbackJobs.length === 0) {
            setSearchMessage(
              `No jobs found for "${keyword}" in ${location} or elsewhere in ${countryName}.`
            );
          } else {
            setSearchMessage(
              `No jobs found for "${keyword}" in ${location} — showing results from elsewhere in ${countryName}.`
            );
          }
        } else if (location) {
          setSearchMessage(`No jobs found for "${keyword}" in ${location}.`);
        } else if (countryCode) {
          setSearchMessage(`No jobs found for "${keyword}" in ${countryName}.`);
        } else {
          setSearchMessage(`No jobs found for "${keyword}". Try adding a city or country.`);
        }
      }


       // kick off usage tracking in the background – don't block the UI
      (async () => {
        try {
          await track("search");
          setUsage(await getUsage());
        } catch (e) {
          console.error("Usage tracking (search) failed:", e);
        }
      })();
    } catch (e) {
      console.error(e);
      alert("AI search failed. Please try again.");
    } finally {
      setJobSearchLoading(false);
    }
  };

  // --- MANUAL SEARCH (keyword / URL / RSS) ---
  const searchJobs = async () => {
    if (!canUse("search", userPlan)) {
      onHitPaywall?.();
      alert("Free plan limit reached today. Upgrade for unlimited searches.");
      return;
    }

    try {
      //setJobSearchLoading(true);
      setSearchResults([]);
      setSearchMessage(""); 
      
      setJobCountry("");
      setVisibleCount(PAGE_SIZE);
      setPage(1);

      // Use RSS only if the user hasn't specified a location
      const useRss = !jobLocation && !jobCountry;

      const results = await runSearch({
        keyword: jobKeyword || "software engineer",
        location: jobLocation,
        countryCode: jobCountry,
        url: jobUrl,
        rssUrl: rssUrl,
        useRss,
      });

      // HERE ► manual “no results” message
      if (!results || results.length === 0) {
        const where =
          jobLocation?.trim() ||
          CODE_TO_NAME.get(jobCountry) ||
          (jobCountry ? jobCountry.toUpperCase() : "");
        setSearchMessage(
          where
            ? `No jobs found for "${jobKeyword || "software engineer"}" in ${where}.`
            : `No jobs found for "${jobKeyword || "software engineer"}".`
        );
      }

      (async () => {
        try {
          await track("search");
          setUsage(await getUsage());
        } catch (e) {
          console.error("Usage tracking (manual search) failed:", e);
        }
      })();
    } finally {
      setJobSearchLoading(false);
    }
  };

  // Decide which search to run based on what the user filled in
  const handleSearchClick = async () => {
    // don't start another search if one is already running
    if (jobSearchLoading) return;

    setJobSearchLoading(true);
    try {
      if (naturalQuery.trim()) {
        await smartSearch();   // AI search
      } else {
        await searchJobs();    // Manual search
      }
    } finally {
      setJobSearchLoading(false);
    }
  };

    const handleSearchButtonClick = async () => {
      // if user hit the free limit, open the upgrade/paywall
      if (searchLimitReached) {
        onHitPaywall?.();
        return;
      }

      // otherwise run the normal search flow
      await handleSearchClick();
    };

  // Restore this function:
  const applyJobToAnalyzer = (j: any) => {
    setJd(`${j.title}\n${j.company ?? ""}\n${j.location ?? ""}\n\n${j.jd_text ?? ""}`);

    // scroll into view + flash
    requestAnimationFrame(() => {
      jdRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      setFlashJD(true);
      setTimeout(() => setFlashJD(false), 900);
    });
  };

  
  async function applyIntentFromBackendToFields(sourceText?: string) {
    const text = (sourceText ?? naturalQuery).trim();
    if (!text) {
      alert(
        "Describe what you want first (type or use the mic, e.g. 'Nurse jobs in Paris')."
      );
      return;
    }

    try {
      const r = await fetch(`${API}/api/ai/intent`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ text }),
      });
      const intent = await r.json();
  
      // country and location into the visible inputs
      const code = normalizeCountryCode((intent.country ?? "").toString());
      if (code) setJobCountry(code);
      if (intent.location) setJobLocation(intent.location);

      const combinedKeywords = [
        intent.role_type || "",
        ...(Array.isArray(intent.keywords) ? intent.keywords : []),
      ].join(" ").trim();
      if (combinedKeywords) setJobKeyword(combinedKeywords);

      //  Employment type (same logic as smartSearch)
      let t: JobType = "Any";
      const rawType = (intent.employment_type || intent.job_type || "")
        .toString()
        .toLowerCase();

      if (rawType.includes("intern")) {
        t = "Internship";
      } else if (rawType.includes("contract")) {
        t = "Contract";
      } else if (rawType.includes("freelanc")) {
        t = "Freelance";
      } else if (rawType.includes("temp")) {
        t = "Temporary";
      } else if (rawType.includes("part")) {
        t = "Part-time";
      } else if (rawType.includes("full")) {
        t = "Full-time";
      } else {
        t = detectEmploymentTypeFromText(text); // fall back to the user’s sentence
      }

      setJobType(t);

      setSearchMessage("Intent applied to fields. Adjust anything and click Search.");
    } catch (e) {
      console.error(e);
      alert("Couldn’t parse your intent right now.");
    }
  }

 // 🔹 Turn a natural-language request into search fields
  const applyIntentToSearch = () => {
    if (!intent.trim()) return;

    setJobCountry("");
    setJobLocation("");

    const text = intent.toLowerCase();
    // --- 1) Guess location & country (generic) -----------------
    let detectedLocation = "";
    //let detectedCountries = jobCountry || "us,gb,se,de,fr";
    let detectedCountries = ""; 

    // generic country detector (covers all ADZUNA_COUNTRIES)
    const detectedCode = detectCountryFromText(intent); // function lowercases internally
    if (detectedCode) {
      detectedLocation = "";         // don't put a country into the city box
      detectedCountries = detectedCode;
    }

    // (Optional) a couple of city hints you still want to keep
    if (text.includes("stockholm")) {
      detectedLocation = "Stockholm";
      detectedCountries = "se";
    }

    // --- 2) Guess keywords ----------------------------
    const stopwords = new Set([
      "i","im","i'm","looking","searching","want","wanna","would","like","to","for","a","an","the",
      "job","jobs","role","position","as","with","around","near","in","at","on","of",
      "find","help","me","get","my","background","based","based in","remote","hybrid",
      "and","or","that","which"
    ]);

    const allWords = text
      .replace(/[^a-z0-9\s]/g, " ")
      .split(/\s+/)
      .map(w => w.trim())
      .filter(w => w.length > 2);

    const jobWords = allWords.filter(w => !stopwords.has(w));

    // prioritize some role words
    jobWords.sort((a, b) => {
      const priority = ["nurse","engineer","developer","scientist","ml","ai","python"];
      const aPri = priority.includes(a) ? 1 : 0;
      const bPri = priority.includes(b) ? 1 : 0;
      return bPri - aPri;
    });

    const keywordGuess = jobWords.slice(0, 8).join(" ");
    if (keywordGuess) {
      setJobKeyword(keywordGuess);
    } else {
      const fallback = allWords.filter(w => w.length > 4).slice(0, 5).join(" ");
      if (fallback) setJobKeyword(fallback);
    }

    //if (detectedLocation) setJobLocation(detectedLocation);
    //setJobCountry(detectedCountries)
    if (detectedLocation) setJobLocation(detectedLocation);
    if (detectedCountries) setJobCountry(detectedCountries); // guard


    // Optional: scroll to the job search card
    const el = document.getElementById("jobs");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };


  // Resume analyzer with plan-limit enforcement + tracking
  const submit = async () => {
    if (!canUse("analyze", userPlan)) {
      onHitPaywall?.();
      alert("Free plan daily analysis limit reached. Upgrade to Premium.");
      return;
    }
    if (!jd && !resume) {
      alert("Add a Job Description or Resume first.");
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      console.log("[analyze] API =", API, "jd len =", jd.length, "resume len =", resume.length);

      const res = await fetchWithTimeout(`${API}/analyze`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_description: jd,
          resume_text: resume,
          role_level: "mid",
          voice_style: "neutral",
          lang: uiLang,           // ← NEW line
          // jd_json, resume_json can be added here later
        }),
      }, 30000);

      const raw = await res.text(); // read body once
      if (!res.ok) {
        console.error("[analyze] HTTP", res.status, raw);
        throw new Error(`Analyze failed (${res.status}) ${raw || ""}`);
      }

      let data: AnalyzeResponse | null = null;
      try {
        data = JSON.parse(raw);
      } catch (e) {
        console.error("[analyze] JSON parse error. Raw body:", raw);
        throw new Error("Analyze returned invalid JSON");
      }
     
      setResult(data!);
      
      const recentAnalysis = {
        id: crypto.randomUUID(),
        title: "Resume Analysis",
        score: data?.match_score ?? 0,
        createdAt: new Date().toISOString(),
      };

      const existingAnalyses = JSON.parse(
        localStorage.getItem("jobtailor_recent_analyses") ?? "[]"
      );

      const updatedAnalyses = [
        recentAnalysis,
        ...(Array.isArray(existingAnalyses) ? existingAnalyses : []),
      ].slice(0, 3);

      localStorage.setItem(
        "jobtailor_recent_analyses",
        JSON.stringify(updatedAnalyses)
      );

      window.dispatchEvent(new Event("dashboard:analyses-changed"));

      requestAnimationFrame(() => {
        resultRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        setFlashResult(true);
        setTimeout(() => setFlashResult(false), 900);
      });

      (async () => {
        try {
          await track("analyze");
          setUsage(await getUsage());
          window.dispatchEvent(new Event("dashboard:usage-changed"));
        } catch (e) {
          console.error("Usage tracking (analyze) failed:", e);
        }
      })();
    } catch (err: any) {
      console.error("Analyze error:", err);
      setError(err?.message || "Analyze failed.");
    } finally {
      setLoading(false);
    }
  };

    const handleAnalyzeClick = async () => {
      // If free limit reached, open paywall instead of running analyze
      if (analysisLimitReached) {
        onHitPaywall?.();
        return;
      }

      await submit();
    };

  // AI CV Coach
  const generateCoachCv = async () => {
    if (!coachInput.trim()) {
      alert("Please tell JobTailor about your background first.");
      return;
    }

    const profile = `
  User CV background:
  ${coachInput}
  `;

    setCvLoading(true);
    try {
      setCvInput(profile);

      const res = await fetch(`${API}/api/generate-cv`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          raw_text: profile,
          lang: coverLang,
        }),
      });

      if (!res.ok) {
        const t = await res.text();
        throw new Error(t);
      }

      const data = await res.json();
      setGeneratedCv(data.cv_text || "");
      const currentCvCount = Number(
        localStorage.getItem("jobtailor_cvs_created_count") ?? "0"
      );

      localStorage.setItem(
        "jobtailor_cvs_created_count",
        String(currentCvCount + 1)
      );

      window.dispatchEvent(new Event("dashboard:cv-created"));
      setShowCvCoach(false);
    } catch (e) {
      console.error(e);
      alert("Could not generate CV. Please try again.");
    } finally {
      setCvLoading(false);
    }
  };

  //  INSERT YOUR COVER LETTER FUNCTION **RIGHT HERE**
  const generateCoverLetter = async () => {
    if (!jd || !resume) {
      alert("Please provide both Job Description and Resume text first.");
      return;
    }

    setGenLoading(true);
    try {
      // Cover letter
      const richness = userPlan === "premium" ? "rich" : "short";

      const res = await fetchWithTimeout(`${API}/generate/cover-letter`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          job_description: jd,
          resume_text: resume,
          role_level: "mid",
          //voice_style: "neutral",
          tone: coverTone,   // NEW
          lang: coverLang,   // NEW – independent from search UI
          //richness, 
        }),
      }, 30000);
      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`Cover-letter failed (${res.status}) ${t}`);
      }
      const data = await res.json();
      setCoverLetter(data.cover_letter || "");

      const currentCount = Number(
        localStorage.getItem("jobtailor_cover_letters_count") ?? "0"
      );

      localStorage.setItem(
        "jobtailor_cover_letters_count",
        String(currentCount + 1)
      );

      window.dispatchEvent(new Event("dashboard:cover-letter-changed"));

    } catch (e) {
      console.error(e);
      alert("Couldn't generate the cover letter. Please try again.");
    } finally {
      setGenLoading(false);
    }
  };

  // put this near your other handlers (e.g., after generateCoverLetter)
  const quickSummary = async (j: any) => {
    setSummaryFor(j);
    setSummError(null);
    setSummaryData(null);
    setSummLoading(p => ({ ...p, [j.url]: true }));
    
    console.log("[summary] API base =", API);
    console.log("[summary] POST", `${API}/api/generate/summary`);
    try {
      const res = await fetch(`${API}/api/generate/summary`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          //   use `text` instead of `job_description`
          text: `${j.title}\n${j.company ?? ""}\n${j.location ?? ""}\n\n${j.jd_text ?? ""}`,
          url: j.url ?? null,
        }),
      });

      if (!res.ok) {
        const t = await res.text().catch(() => "");
        throw new Error(`HTTP ${res.status} ${t}`);
      }

      const data = await res.json();
      setSummaryData(data); // modal will render this
    } catch (e: any) {
      console.error("Quick summary failed:", e);
      setSummError(e?.message || "Failed to fetch");
    } finally {
      setSummLoading(p => ({ ...p, [j.url]: false }));
    }
  };


  // what: 'results' | 'cover' | 'both'
  const handleExport = async (what: "results" | "cover" | "both" = "both") => {
    try {
      if (what === "results" && !result) {
        alert("No analysis results to export yet.");
        return;
      }
      if (what === "cover" && !coverLetter) {
        alert("No cover letter to export yet.");
        return;
      }

      const payload: any = { what, uiLang };
      if (what === "results" || what === "both") payload.results = result;
      if (what === "cover" || what === "both") payload.coverLetter = coverLetter;

      const res = await fetch(`${API}/api/export/pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });

      if (!res.ok) {
        const msg = await res.text().catch(() => "");
        alert(`Export failed (${res.status}): ${msg || "Unknown error"}`);
        return;
      }

      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);

      // Either open in a new tab…
      window.open(url, "_blank");

      // …or trigger a download instead:
      // const a = document.createElement("a");
      // a.href = url;
      // a.download = what === "cover" ? "jobtailor-cover-letter.pdf" : "jobtailor-results.pdf";
      // document.body.appendChild(a);
      // a.click();
      // a.remove();

      // Clean up
      setTimeout(() => URL.revokeObjectURL(url), 10_000);
    } catch (err) {
      console.error("[export] error", err);
      alert("Export failed – check the console for details.");
    }
  };

  // NEW: Export generated CV as PDF
  const handleExportCv = async () => {
    if (!generatedCv) {
      alert("No CV to export yet.");
      return;
    }

    const payload: any = {
      what: "cv",
      uiLang,
      cvText: generatedCv,
    };

    const res = await fetch(`${API}/api/export/pdf`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });

    if (!res.ok) {
      const msg = await res.text().catch(() => "");
      alert(`Export failed (${res.status}): ${msg || "Unknown error"}`);
      return;
    }

    const blob = await res.blob();
    const url = window.URL.createObjectURL(blob);
    window.open(url, "_blank");
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };



  // pick limits based on active plan for the badge only
  const cap = userPlan === "premium" ? Limits.premium : Limits.free;

  const isFree = userPlan === "free";

  const searchLimitReached =
    isFree && usage.search >= cap.dailySearch;

  const analysisLimitReached =
    isFree && usage.analyze >= cap.dailyAnalyze;

  const totalResults = searchResults.length;
  const totalPages = Math.max(1, Math.ceil(totalResults / PAGE_SIZE));
  const safePage = Math.min(Math.max(page, 1), totalPages);

  const startIndex = (safePage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalResults);
  const pageResults = searchResults.slice(startIndex, endIndex);
 
  return (
    <div className="analyzer-root max-w-3xl mx-auto p-6 space-y-6">
      {/* <-- add the marker right here */}
      <div data-analyzer-version="v-2025-12-04-1" />
      <h1 className="text-2xl font-semibold">JobTailor - AI Job Search &amp; Resume/CV Assistant!</h1>
      
      {/* Usage badge */}
      <span className="text-sm text-gray-700 dark:text-gray-300 block">
        {userPlan === "premium"
          ? "Searches: Unlimited • Analyses: Unlimited"
          : `Searches: ${usage.search}/${cap.dailySearch} • Analyses: ${usage.analyze}/${cap.dailyAnalyze}`}
      </span>

      <div className="space-y-2">
        <label className="font-medium">Job Description</label>
        <textarea
          ref={jdRef}
          id="jd-input"
          className={`w-full rounded border border-border p-3 min-h-[160px]
                      bg-white text-black placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-primary/40
                      dark:bg-white dark:text-black dark:placeholder-gray-400
                      ${flashJD ? "ring-2 ring-primary/60" : ""}`}
          value={jd}
          onChange={(e) => setJd(e.target.value)}
          placeholder="Paste the JD text here…"
        />

      </div>
      {/* Actions for JD (right under the JD) */}
      <div className="flex items-center gap-3 ml-auto text-xs whitespace-nowrap">
        <button
          onClick={handleAnalyzeClick}
          disabled={loading || (!jd && !resume)}  // remove analysisLimitReached here
          title={
            analysisLimitReached
              ? "Free daily analysis limit reached. Upgrade to analyze more."
              : (resume
                  ? "Analyze the JD vs your resume"
                  : "Add your resume for a meaningful match score")
          }
          className="px-4 py-2 rounded bg-blue-600 text-white font-semibold disabled:opacity-50"
        >
          {analysisLimitReached
            ? "Upgrade to analyze more"
            : (loading ? "Analyzing…" : "Analyze")}
        </button>

        <button
          onClick={() => {
            setShowCvCoach((v) => !v);
            setTimeout(() => {
              document.getElementById("cv-coach")?.scrollIntoView({
                behavior: "smooth",
                block: "start",
              });
            }, 50);
          }}
          className="px-4 py-2 rounded bg-blue-600 text-white font-semibold disabled:opacity-50"
        >
          {showCvCoach ? "Hide Create CV" : "Create CV"}
        </button>

        <button
          onClick={generateCoverLetter}
          disabled={genLoading || !jd || !resume}
          className="px-4 py-2 rounded bg-primary text-white font-semibold disabled:opacity-50"
          title={!resume ? "Paste/upload your resume first" : "Generate a tailored letter from the JD + your resume"}
        >
          {genLoading ? "Generating…" : "Generate Cover Letter"}
        </button>

      {/* NEW: tone + language controls for cover letter */}
        <div className="flex items-center gap-3 ml-auto text-sm whitespace-nowrap">
          <label className="flex items-center gap-1">
            Tone:
            <select
              value={coverTone}
              onChange={(e) => setCoverTone(e.target.value as any)}
              className="border rounded px-2 py-1 bg-white text-black
                         dark:bg-white dark:text-black
                         dark:[&>option]:bg-white dark:[&>option]:text-black"
            >
              <option value="neutral">Neutral</option>
              <option value="formal">Formal</option>
              <option value="friendly">Friendly</option>
              <option value="concise">Very concise</option>
            </select>
          </label>

          <label className="flex items-center gap-1">
            Languages:
            <select
              value={coverLang}
              onChange={(e) => setCoverLang(e.target.value as any)}
              className="border rounded px-2 py-1 bg-white text-black
                         dark:bg-white dark:text-black
                         dark:[&>option]:bg-white dark:[&>option]:text-black"
            >
              <option value="en">English</option>
              <option value="sv">Svenska</option>
              <option value="de">Deutsch</option>
              <option value="fr">Français</option>
              <option value="es">Español</option>
            </select>
          </label>
        </div>
      </div> 

      {/*  Put RESULTS right here */} 
      {result && (
        <div
          ref={resultRef}
          className={`results-card mt-3 rounded-xl border border-border bg-panel p-4 space-y-3
                      text-text dark:text-gray-100
                      ${flashResult ? "ring-2 ring-primary/60" : ""}`}
        >
          <div className="flex items-center justify-between">
            <h2 className="text-xl font-semibold text-text dark:text-gray-100">Results</h2>

            <div className="flex items-center gap-2">
              {/* small, subtle ghost button */}
              <button
                onClick={() => handleExport("results")}
                disabled={!result}                 // keep this simple
                title="Export analysis as PDF"
                className="text-xs underline text-primary dark:text-blue-300 disabled:opacity-50 ml-2"
              >
                Export
              </button>

              <button
                type="button"
                onClick={() => setResult(null)}
                className="text-xs underline text-primary"
                title="Clear analyze results"
              >
                Clear
              </button>
            </div>
          </div>

          <p><span className="font-medium">Match:</span> {result.match_score}%</p>

          {Array.isArray(result.top_common_keywords) && result.top_common_keywords.length > 0 && (
            <p>
              <span className="font-medium">Top common keywords:</span>{" "}
              {result.top_common_keywords.join(", ")}
            </p>
          )}

          {Array.isArray(result.missing_keywords) && result.missing_keywords.length > 0 && (
            <p>
              <span className="font-medium">Missing keywords:</span>{" "}
              {result.missing_keywords.join(", ")}
            </p>
          )}

          {Array.isArray(result.suggestions) && result.suggestions.length > 0 && (
            <div className="space-y-2">
              <h3 className="font-medium">Bullet rewrites</h3>
              <ul className="list-disc pl-5 space-y-2">
                {result.suggestions.map((b, i) => (
                  <li key={i}>
                    <div><span className="font-semibold">Original:</span> {b.original}</div>
                    <div><span className="font-semibold">Rewrite:</span> {b.rewrite}</div>
                    <div className="text-sm text-subtext dark:text-gray-400">
                      Reason: {b.reason}
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {/* (Optional) Show the generated letter right here too */}
      {coverLetter && (
        <div 
          ref={coverRef}
          className="cover-card mt-3 rounded-xl border border-border bg-panel p-4 space-y-3 text-text dark:text-gray-100">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold">Cover Letter</h3>

            <div className="flex items-center gap-2">
              <button
                onClick={() => handleExport("cover")}
                disabled={!coverLetter}
                title="Export cover letter as PDF"
                className="text-xs underline text-primary dark:text-blue-300 disabled:opacity-50 ml-2"
              >
                Export
              </button>

              <button
                onClick={() => setCoverLetter("")}
                className="text-xs underline text-primary"
                title="Clear cover letter"
              >
                Clear
              </button>
            </div>
          </div>
          <textarea
            className="w-full rounded border border-border p-3 min-h-[420px]
                      bg-white text-black placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-primary/40
                      dark:bg-white dark:text-black dark:placeholder-gray-400"
            value={coverLetter}
            onChange={(e) => setCoverLetter(e.target.value)}
          />
        </div>
      )}

      {showCvCoach && (
        <div id="cv-coach" className="rounded-xl border border-border bg-panel p-4 space-y-3">
          <h2 className="text-lg font-semibold">AI CV Coach</h2>

          <p className="text-sm text-subtext dark:text-gray-300">
            Tell JobTailor about yourself in your own words. You can write naturally,
            paste an old CV, paste your LinkedIn profile, or describe your work
            experience, education, skills, projects, certifications and career goals.
          </p>

          <p className="text-xs text-gray-400">
            Contact details, LinkedIn, languages, certifications, projects and hobbies
            are optional. Only include information you want to appear in your CV.
          </p>

          <p className="text-xs text-gray-400">
            The more real details you provide, the stronger and more tailored your CV
            will be. JobTailor will organize everything into a professional,
            ATS-friendly CV.
          </p>

          <textarea
            className="w-full rounded border p-3 min-h-[300px] bg-white text-black"
            value={coachInput}
            onChange={(e) => setCoachInput(e.target.value)}
            placeholder={`Example:

            I am John Erikson from Stockholm.

            Contact:
            john@email.com
            0701234567
            LinkedIn: linkedin.com/in/abbe-erikson

            I am targeting Project Manager roles.

            I worked at ABC Logistics as an Operations Assistant from 2022 to present.
            I coordinate shipments and support logistics operations.
            I use Excel and Power BI for reporting.

            I have a Bachelor of Business Administration from the University of Nairobi, 2022.

            Skills:
            Excel
            Power BI
            Customer Service

            Languages:
            English
            Swedish

            Certifications:
            PMP
            Scrum Master

            Projects / Highlights:
            Built a Power BI dashboard for logistics reporting.

            Volunteer work / interests:
            Volunteer mentor
            Football
            Technology
            Travel`}
          />

          <button
            onClick={generateCoachCv}
            disabled={cvLoading || !coachInput.trim()}
            className="px-4 py-2 rounded bg-blue-600 text-white font-semibold disabled:opacity-50"
          >
            {cvLoading ? "Creating CV…" : "Generate CV"}
          </button>
        </div>
      )}
      {generatedCv && (
        <div className="rounded-xl border border-border bg-panel p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold">Generated CV</h3>

            <div className="flex items-center gap-2">
              <button
                onClick={handleExportCv}
                disabled={!generatedCv}
                className="text-xs underline text-primary dark:text-blue-300 disabled:opacity-50"
              >
                Export
              </button>

              <button
                onClick={() => setGeneratedCv("")}
                className="text-xs underline text-primary"
              >
                Clear
              </button>
            </div>
          </div>

          <textarea
            className="w-full rounded border border-border p-3 min-h-[260px] bg-white text-black"
            value={generatedCv}
            onChange={(e) => setGeneratedCv(e.target.value)}
          />

          <button
            onClick={() => setResume(generatedCv)}
            className="px-3 py-2 rounded border"
          >
            Use as Resume Text
          </button>
        </div>
      )}

      {/* Hint when no resume is present */}
      {!resume && (
        <p className="mt-1 text-xs text-amber-600 dark:text-amber-400">
          Tip: paste your resume below or upload a file so we can compute a real match and craft a tailored cover letter.
        </p>
      )}

      {/* Resume text paste */}
      <div className="space-y-2">
        <label className="font-medium">Resume / CV for Analysis</label>
        <textarea
          className="w-full rounded border border-border p-3 min-h-[160px]
                    bg-white text-black placeholder-gray-400
                    focus:outline-none focus:ring-2 focus:ring-primary/40
                    dark:bg-white dark:text-black dark:placeholder-gray-400"
          value={resume}
          onChange={(e) => {
            const v = e.target.value;
            setResume(v);

            if (v.trim() === "") {
              // clear JS state
              setResumeFile(null);
              // clear the native <input type="file">
              if (resumeInputRef.current) resumeInputRef.current.value = "";
              // (optional) force remount for Safari/Edge quirks
              setFileInputKey((k) => k + 1);
            }
          }}
          placeholder="Paste your resume text here…"
        />


      </div>
      <div className="space-y-2">
        <label className="font-medium">Upload Resume (optional)</label>
        <input
          key={fileInputKey}                 // forces a reset in stubborn browsers
          ref={resumeInputRef}              // lets us clear the native value
          type="file"
          accept=".pdf,.doc,.docx"
          onChange={handleResumeUpload}
        />
        {resumeFile && (
          <p className="selected-file-text text-sm text-text dark:text-gray-200">Selected: {resumeFile.name}</p>
        )}
      </div>
      {/* --- Job Search (global) --- */}
      <div
        id="jobs"
        className="rounded-2xl border border-border bg-panel p-4 space-y-4"
      >
        <h2 className="text-lg font-semibold text-text dark:text-gray-100">
          Search Jobs
        </h2>

        {/* AI INTENT INPUT */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <label className="font-medium">
              Describe what you want (AI) <span className="text-xs text-text"></span>
            </label>

            <div className="flex items-center gap-2">
              {/* language dropdown (make text black on white) */}
              <select
                value={mic.lang}
                onChange={(e) => {
                  const v = e.target.value;
                  mic.setLang(v);
                  // keep UI language in sync ("en","sv","de","fr"...)
                  setUiLang(mapSpeechToShort(v));   // ← CHANGED: ensure this updates uiLang
                }}
                className="h-9 border rounded px-2 bg-white text-black text-sm
                          [&>option]:bg-white [&>option]:text-black
                          dark:bg-white dark:text-black
                          dark:[&>option]:bg-white dark:[&>option]:text-black"
                title="Recognition language"
              >
                <option value="en-US">English (US)</option>
                <option value="en-GB">English (UK)</option>
                <option value="sv-SE">Svenska</option>
                <option value="de-DE">Deutsch</option>
                <option value="fr-FR">Français</option>
                <option value="es-ES">Español</option>
              </select>
            </div>
          </div>

          {/* Relative container so the mic can float inside */}
          <div className="relative">
            <textarea
              className="w-full rounded border border-border pr-14 p-3 min-h-[110px]
                        bg-white text-black placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary/40
                        dark:bg-white dark:text-black dark:placeholder-gray-400"
              placeholder='Search or Write For Example: "I am looking AI-developer job in Stockholm."'
              value={naturalQuery}
              onChange={(e) => setNaturalQuery(e.target.value)}
            />

            {/* floating mic in the bottom-right corner */}
            <div className="absolute bottom-2 right-2 flex items-center gap-2">
              {/* Listening badge (only when active) */}
              {mic.listening && (
                <span className="text-[11px] px-2 py-0.5 rounded-full border bg-panel text-text">
                  Listening…
                </span>
              )}

              <MicButton
                listening={mic.listening}
                supported={mic.supported}
                onToggle={() => {
                  if (!mic.supported) {
                    alert("Voice input not supported in this browser.");
                    return;
                  }
                  if (mic.listening) {
                    mic.stop();
                    return;
                  }

                  // Start a new dictation ⇒ clear previous text
                  setNaturalQuery("");

                  mic.start(
                    (finalText) => {
                      if (!finalText) return;
                      const clean = finalText.trim();
                      setNaturalQuery(clean);
                      applyIntentFromBackendToFields(clean);
                    },
                    (partial) => {
                      setNaturalQuery(partial?.trim() || "");
                    }
                  );
                }}
                title="Speak your query"
              />
            </div>
          </div>
        </div>   

        {/* Location + Countries */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <div>
            <label className="font-medium">Location (optional)</label>
            <input
              type="text"
              value={jobLocation}
              onChange={(e) => setJobLocation(e.target.value)}
              placeholder="e.g. Stockholm, London, Remote"
              className="w-full rounded border border-border p-3
                        bg-white text-black placeholder-gray-400
                        focus:outline-none focus:ring-2 focus:ring-primary/40
                        dark:bg-white dark:text-black dark:placeholder-gray-400"
            />
          </div>
          <div>
            <label className="font-medium">Employment type</label>
            <select
              className={`w-full border p-2 rounded bg-white
                          ${jobType === "Any" ? "text-gray-400" : "text-black"}
                          dark:bg-white
                          dark:[&>option]:bg-white dark:[&>option]:text-black`}
              value={jobType}
              onChange={(e) => setJobType(e.target.value as JobType)}
            >
              <option>Any</option>
              <option>Full-time</option>
              <option>Part-time</option>
              <option>Contract</option>
              <option>Internship</option>
              <option>Temporary</option>
              <option>Freelance</option>
            </select>
          </div>
          <div>
            <label className="font-medium">Country</label>
            <select
              className={`w-full border p-2 rounded bg-white
                          ${jobCountry === "" ? "text-gray-400" : "text-black"}
                          dark:bg-white
                          dark:[&>option]:bg-white dark:[&>option]:text-black`}
              value={jobCountry}
              onChange={(e) => setJobCountry(e.target.value)}
            >
              <option value="">Select country…</option>
              {ADZUNA_COUNTRIES.map((c) => (
                <option key={c.code} value={c.code}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="space-y-2">
          <label className="font-medium">Job Posting URL (optional)</label>
          <input
            type="text"
            value={jobUrl}
            onChange={(e) => setJobUrl(e.target.value)}
            placeholder="Paste a job URL that has Schema.org JobPosting"
            className="w-full rounded border border-border p-3
                      bg-white text-black placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-primary/40
                      dark:bg-white dark:text-black dark:placeholder-gray-400"
          />
        </div>

        <div className="space-y-2">
          <label className="font-medium">RSS Feed URL (optional)</label>
          <input
            type="text"
            value={rssUrl}
            onChange={(e) => setRssUrl(e.target.value)}
            placeholder="Paste an RSS/Atom job feed"
            className="w-full rounded border border-border p-3
                      bg-white text-black placeholder-gray-400
                      focus:outline-none focus:ring-2 focus:ring-primary/40
                      dark:bg-white dark:text-black dark:placeholder-gray-400"
          />
        </div>

        {/* Optional: keep only ONE of these Search buttons if you want */}
        <button
          onClick={handleSearchButtonClick}
          disabled={
            jobSearchLoading ||
            (
              !naturalQuery.trim() &&
              !jobKeyword &&
              !jobUrl &&
              !rssUrl
            )
          }  // ← notice: searchLimitReached removed from disabled
          className="px-4 py-2 rounded bg-green-600 text-white disabled:opacity-50"
        >
          {searchLimitReached
            ? "Upgrade to search more"
            : (jobSearchLoading ? "Searching…" : "Search Jobs")}
        </button>

        <button
          onClick={() => applyIntentFromBackendToFields()}
          disabled={!naturalQuery.trim() || jobSearchLoading}
          className="intent-button ml-2 px-3 py-2 rounded border text-sm"
          title="Fill the fields using AI, without running a search"
        >
          Apply intent to fields
        </button>


        {searchMessage && (
          <p className="search-message pt-2 text-sm">
            {searchMessage}
          </p>
        )}

        {totalResults > 0 && (
          <div className="pt-4">
            <h3 className="results-heading text-lg font-semibold text-text dark:text-gray-100">
              Results{" "}
              <span className="font-normal text-subtext dark:text-gray-300">
                ({totalResults})
              </span>
            </h3>

                  
            <ul className="space-y-3 pt-2">
              {pageResults.map((j: any, i: number) => {
                const flag = getFlagFromSource(j.source_code);
                const mode = detectWorkMode(j);
                const salary = extractSalary(j.jd_text);

                return (
                  <li
                    key={i}
                     className="results-card relative group rounded-xl2 border border-border bg-panel p-4 hover:shadow-soft transition-shadow"
                  >
                    {/* Save pill – top-right, green when saved */}
                    <button
                      onClick={() => {
                        if (!requireSignIn()) return;                 // <-- guard + redirect
                        saved[j.url] ? unsaveJobLocal(j.url) : saveJobLocal(j);
                      }}
                      disabled={!isAuthed || savingId === j.url}      // also disable when logged out
                      className={`absolute top-2 right-2 text-xs px-3 py-1 rounded-full
                        ${saved[j.url] ? "bg-green-600 text-white" : "bg-gray-200 text-gray-800 hover:bg-gray-300"}
                        ${!isAuthed ? "opacity-60 cursor-not-allowed" : ""}`}
                      title={isAuthed ? "" : "Sign in to save jobs"}
                    >
                      {savingId === j.url ? "Saving…" : saved[j.url] ? "Saved" : "Save"}
                    </button>

                    {/* Title */}
                    <div className="font-semibold pr-20 text-text dark:text-white">
                      {j.title}
                    </div>

                    {/* Company • Location row */}
                    <div 
                      className="
                        text-sm mt-0.5 flex items-center gap-2 flex-wrap
                        text-gray-700 dark:text-gray-300   /* default tone for the row */
                      "
                    >
                      <span className="text-gray-900 dark:text-gray-100">
                        {(j.company || "").toString()}
                      </span>

                      {j.location && (
                        <span className="text-gray-600 dark:text-gray-300">
                          • {j.location}
                        </span>
                      )}

                      {/* chips… */}

                      {/* Flag + Work mode + Salary + Match chips */}
                      <>
                        {flag && <span className="ml-1">{flag}</span>}
                        {mode && (
                          <span className="job-chip-remote text-xs rounded-full px-2 py-0.5">
                            {mode}
                          </span>
                        )}
                        {salary && (
                          <span className="text-xs rounded-full px-2 py-0.5
                                          bg-yellow-100 text-gray-800
                                          dark:bg-yellow-300 dark:text-gray-900">
                            {salary}
                          </span>
                        )}              
                      </>
                    </div>

                    {/* Snippet */}
                    <p className="text-sm mt-2 text-text dark:text-gray-200 line-clamp-3">
                      {j.jd_text}
                    </p>

                    {/* Actions */}
                    <div className="flex gap-12 mt-3 flex-wrap">
                      <a
                        className="text-primary underline"
                        href={j.url}
                        target="_blank"
                        rel="noreferrer"
                      >
                        View
                      </a>

                       <button
                        onClick={() => applyJobToAnalyzer(j)}
                        title="Fill the Analyzer with this job"
                        className="text-xs px-2 py-1 rounded-md border border-primary text-primary
                                  bg-transparent hover:bg-primary/10"
                      >
                        Use for analysis
                      </button>
                    </div>
                    {ENABLE_QUICK_SUMMARY && (
                      <button
                        className="px-2.5 py-1.5 text-xs rounded-lg border border-border text-text bg-panel hover:shadow-soft disabled:opacity-50"
                        disabled={!!summLoading[j.url]}
                        onClick={() => quickSummary(j)}
                      >
                        {summLoading[j.url] ? "Summarizing…" : "Quick summary"}
                      </button>
                    )}
                  </li>
                );
              })}
            </ul>

            {/* Pagination buttons */}
            <div className="results-pagination flex items-center gap-2 mt-4">
              <button
                type="button"
                className="px-3 py-1.5 rounded border text-sm
                          dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-800
                          disabled:opacity-50"
                disabled={safePage <= 1}
                onClick={() => setPage((p) => Math.max(p - 1, 1))}
              >
                Previous
              </button>

              <button
                type="button"
                className="px-3 py-1.5 rounded border text-sm
                          dark:text-gray-100 dark:border-gray-500 dark:hover:bg-gray-800
                          disabled:opacity-50"
                disabled={safePage >= totalPages}
                onClick={() => setPage((p) => Math.min(p + 1, totalPages))}
              >
                Next
              </button>

              <span className="text-sm text-text dark:text-gray-300">
                Page {safePage} of {totalPages}
              </span>
            </div>

          </div>
        )}
      </div> 
      {summaryFor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/25">
          <div className="summary-card w-full max-w-xl rounded-xl border border-border bg-panel p-4 text-text shadow-2xl">
            {/* Header */}
            <div className="flex items-center justify-between">
              <div className="font-semibold text-text dark:text-gray-100">
                {summaryFor.title}
                <div className="text-xs text-subtext dark:text-gray-300">
                  {(summaryFor.company || "")}
                  {summaryFor.location ? ` • ${summaryFor.location}` : ""}
                </div>
              </div>
              <button
                className="text-subtext hover:text-text"
                onClick={() => { setSummaryFor(null); setSummaryData(null); setSummError(null); }}
                aria-label="Close summary"
              >
                ✕
              </button>
            </div>

            {/* Loading / error */}
            {!summaryData && !summError && (
              <p className="mt-3 text-sm text-subtext dark:text-gray-300">Working…</p>
            )}
            {summError && (
              <p className="mt-3 text-sm text-warn dark:text-red-300">Error: {summError}</p>
            )}

            {/* Content */}
            {summaryData && (
              <div className="mt-3 space-y-4">
                {/* TL;DR */}
                <div>
                  <h4 className="font-medium text-text dark:text-gray-100">TL;DR</h4>
                  <ul className="list-disc ml-5 text-sm text-text dark:text-gray-200">
                    {summaryData.tldr?.map((b, i) => <li key={i}>{b}</li>)}
                  </ul>
                </div>

                {/* Must-haves */}
                {!!summaryData.must_haves?.length && (
                  <div>
                    <h4 className="font-medium text-text dark:text-gray-100">Must-have skills</h4>
                    <div className="mt-1 flex flex-wrap gap-2">
                      {summaryData.must_haves.map((t, i) => (
                        <span
                          key={i}
                          className="text-xs rounded-full px-2 py-0.5 bg-primary/10 text-text dark:text-gray-100"
                        >
                          {t}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                {!!summaryData.concerns?.length && (
                  <div>
                    <h4 className="font-medium">Concerns</h4>
                    <ul className="list-disc ml-5 text-sm">
                      {summaryData.concerns.map((c, i) => <li key={i}>{c}</li>)}
                    </ul>
                  </div>
                )}

                <div className="pt-2 text-right">
                  <button
                    className="px-3 py-1.5 rounded-lg border border-border text-text hover:shadow-soft"
                    onClick={() => { setSummaryFor(null); setSummaryData(null); setSummError(null); }}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
        
    </div>
  );
}
